import React, { useState } from "react";
import { TestDashboard } from "./TestDashboard";

interface TestsTabProps {
  data: any;
}

export default function TestsTab({ data }: TestsTabProps) {
  const [selectedPhase, setSelectedPhase] = useState<string>("all");
  const [selectedTest, setSelectedTest] = useState<any | null>(null);

  if (!data.tests || data.tests.length === 0) {
    return (
      <div className="alert alert-info">No test data available for this step</div>
    );
  }

  let filteredTests = [...data.tests];

  // Filter by phase
  if (selectedPhase !== "all") {
    filteredTests = filteredTests.filter((t: any) => t.phase === selectedPhase);
  }

  const totalAttempts = data.tests.reduce(
    (sum: number, test: any) => sum + (test.attemptCount || 0),
    0
  );
  const averageScore =
    data.tests.reduce(
      (sum: number, test: any) => sum + (test.averageScore || 0),
      0
    ) / (data.tests.length || 1);

  return (
    <div className="tests-tab">
      {/* Summary Cards */}
      <div className="tests-summary">
        <div className="summary-card">
          <div className="summary-icon">📝</div>
          <div className="summary-content">
            <div className="summary-label">Total Tests</div>
            <div className="summary-value">{data.tests.length}</div>
          </div>
        </div>
        <div className="summary-card">
          <div className="summary-icon">🎯</div>
          <div className="summary-content">
            <div className="summary-label">Total Attempts</div>
            <div className="summary-value">{totalAttempts}</div>
          </div>
        </div>
        <div className="summary-card">
          <div className="summary-icon">⭐</div>
          <div className="summary-content">
            <div className="summary-label">Average Score</div>
            <div className="summary-value">{Math.round(averageScore)}%</div>
          </div>
        </div>
      </div>

      {/* Phase Filter */}
      <div className="tests-controls">
        <div className="filter-group">
          <label>Filter by Phase:</label>
          <select
            value={selectedPhase}
            onChange={(e) => setSelectedPhase(e.target.value)}
            className="form-control"
          >
            <option value="all">All Phases</option>
            <option value="pre-class">Pre-Class</option>
            <option value="in-class">In-Class</option>
            <option value="post-class">Post-Class</option>
          </select>
        </div>
      </div>

      {/* Tests Table */}
      <div className="analytics-card">
        <h5>📝 Test Performance ({filteredTests.length})</h5>
        <div className="table-responsive">
          <table className="table table-hover analytics-table">
            <thead>
              <tr>
                <th>Test Name</th>
                <th>Phase</th>
                <th>Attempts</th>
                <th>Unique Students</th>
                <th>Avg Score</th>
                <th>Highest Score</th>
                <th>Lowest Score</th>
                <th>Pass Rate</th>
                <th>Avg Time</th>
                <th>Completion</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredTests.map((test: any, index: number) => (
                <tr key={index}>
                  <td>
                    <strong>{test.testName || "Untitled Test"}</strong>
                  </td>
                  <td>
                    <span className="phase-badge">
                      {test.phase === "pre-class"
                        ? "📖 Pre"
                        : test.phase === "in-class"
                          ? "👩‍🏫 In"
                          : test.phase === "post-class"
                            ? "📝 Post"
                            : "Other"}
                    </span>
                  </td>
                  <td>{test.attemptCount || 0}</td>
                  <td>{test.uniqueStudents || 0}</td>
                  <td>
                    <span
                      className={`score-badge ${
                        test.averageScore >= 70
                          ? "score-good"
                          : test.averageScore >= 50
                            ? "score-average"
                            : "score-poor"
                      }`}
                    >
                      {Math.round(test.averageScore || 0)}%
                    </span>
                  </td>
                  <td className="text-success">
                    {Math.round(test.highestScore || 0)}%
                  </td>
                  <td className="text-danger">
                    {Math.round(test.lowestScore || 0)}%
                  </td>
                  <td>
                    <div className="progress-container">
                      <div
                        className="progress-bar"
                        style={{
                          width: `${Math.round(test.passRate || 0)}%`,
                          backgroundColor:
                            test.passRate >= 70
                              ? "#10b981"
                              : test.passRate >= 50
                                ? "#f59e0b"
                                : "#ef4444",
                        }}
                      />
                      <span className="progress-label">
                        {Math.round(test.passRate || 0)}%
                      </span>
                    </div>
                  </td>
                  <td>{Math.round(test.averageTimeSpent || 0)} min</td>
                  <td>
                    <div className="progress-container">
                      <div
                        className="progress-bar"
                        style={{
                          width: `${Math.round(test.completionRate || 0)}%`,
                        }}
                      />
                      <span className="progress-label">
                        {Math.round(test.completionRate || 0)}%
                      </span>
                    </div>
                  </td>
                  <td>
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() => setSelectedTest(test)}
                      title="View detailed test dashboard"
                    >
                      📊 Dashboard
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Performance Analysis */}
      <div className="analytics-card">
        <h5>📊 Performance Analysis</h5>
        <div className="performance-grid">
          {filteredTests.map((test: any, index: number) => (
            <div key={index} className="performance-card">
              <h6>{test.testName || "Untitled Test"}</h6>
              <div className="performance-metrics">
                <div className="perf-metric">
                  <span className="perf-label">Participation:</span>
                  <span className="perf-value">
                    {test.uniqueStudents || 0} / {data.overview?.totalStudents || 0} students
                  </span>
                  <div className="perf-bar">
                    <div
                      className="perf-bar-fill"
                      style={{
                        width: `${
                          data.overview?.totalStudents > 0
                            ? Math.round(
                                ((test.uniqueStudents || 0) /
                                  data.overview.totalStudents) *
                                  100
                              )
                            : 0
                        }%`,
                      }}
                    />
                  </div>
                </div>
                <div className="perf-metric">
                  <span className="perf-label">Score Distribution:</span>
                  <div className="score-range">
                    <span className="range-label">Low</span>
                    <div className="range-bar">
                      <div
                        className="range-fill"
                        style={{
                          left: `${test.lowestScore || 0}%`,
                          width: `${(test.highestScore || 0) - (test.lowestScore || 0)}%`,
                        }}
                      />
                      <div
                        className="range-avg"
                        style={{ left: `${test.averageScore || 0}%` }}
                        title={`Average: ${Math.round(test.averageScore || 0)}%`}
                      />
                    </div>
                    <span className="range-label">High</span>
                  </div>
                </div>
                <div className="perf-metric">
                  <span className="perf-label">Pass/Fail:</span>
                  <div className="pass-fail-bar">
                    <div
                      className="pass-section"
                      style={{ width: `${test.passRate || 0}%` }}
                    >
                      Pass: {Math.round(test.passRate || 0)}%
                    </div>
                    <div
                      className="fail-section"
                      style={{ width: `${100 - (test.passRate || 0)}%` }}
                    >
                      Fail: {100 - Math.round(test.passRate || 0)}%
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Test Dashboard Modal */}
      {selectedTest && (
        <TestDashboard
          testId={selectedTest.testId}
          testName={selectedTest.testName || "Test"}
          batchId={data.step?.batchId}
          lessonPlanId={data.step?.lessonPlanId}
          stepId={data.step?.id}
          onClose={() => setSelectedTest(null)}
        />
      )}
    </div>
  );
}
